import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "@mui/material";
import SubHeader from "components/common/SubHeader";
import TabHeader from "components/common/TabHeader";
import useUserStore from "@store/useUserStore";
import CoverPage from "components/common/CoverPage";
import LoadingSpinner from "components/common/LoadingSpiner";
import TabWrap from "components/myBuccl/TabWrap";
import ProfileCard from "components/profile/ProfileCard";
import MediaViewer from "components/common/MediaViewer";
import { getProfileV2 } from "api/user";
import { MESSAGING_ENABLED } from "constants/featureFlags";
import { getMessagePaperPlaneIcon } from "assets/images/icons/messagePaperPlane";
import alertIcon from "assets/images/icons/alert.svg";
import useUnreadCounts from "hooks/useUnreadCounts";

const messageIcon = getMessagePaperPlaneIcon('#464a4d');

// 아이콘 우상단 미읽음 뱃지. SubHeader는 노드를 그대로 그리기만 하므로
// 뱃지 표기 기준(0이면 숨김, 9+ 처리)은 이 화면이 결정한다.
const badgedIcon = (icon, count) => (
    <Badge className="sub-header__menu-badge" badgeContent={count > 0 ? count : null} color="error" max={9}>
        <img src={icon} alt="" />
    </Badge>
);

const unreadLabel = (name, count) => (count > 0 ? `${name} ${count}개 읽지 않음` : name);

const MyBuccl = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userInfo = useUserStore((state) => state.userInfo);
    const { notificationCount, messageUnreadCount } = useUnreadCounts();

    const menus = [
      ...(userInfo?.is_staff ? ["강사관리"] : []),
      "나의 활동", "예약", "피드", "커뮤니티",
    ];

    const [selectedTab, setSelectedTab] = useState(() => {
        const tabParam = searchParams.get("tab");
        const defaultTab = userInfo?.is_staff ? "강사관리" : "나의 활동";
        return menus.includes(tabParam) ? tabParam : defaultTab;
    });
    const [profileV2, setProfileV2] = useState(null);
    // v2 프로필 응답 전에 카드가 먼저 그려지면 자격증/버킷리스트/PB가
    // 뒤늦게 붙으며 화면이 출렁여서, 응답이 정리된 뒤 한 번에 렌더한다
    const [isProfileLoading, setIsProfileLoading] = useState(true);
    const [profileViewerOpen, setProfileViewerOpen] = useState(false);

    useEffect(() => {
        if (!userInfo?.user_id) return;
        getProfileV2(userInfo.user_id).then((res) => {
            if (res) setProfileV2(res);
            setIsProfileLoading(false);
        });
    }, [userInfo?.user_id]);

    // 공개 버킷리스트는 v2 통합 프로필 응답에 포함 (별도 호출 제거)
    const bucketList = profileV2?.bucket_list ?? [];
    

    const profile = userInfo
        ? {
              user_id: userInfo.user_id,
              name: userInfo.name,
              nickname: userInfo.nickname,
              profile_image_url: userInfo.profile_image_url,
              bio: profileV2?.bio ?? userInfo.bio,
              level: profileV2?.level ?? userInfo.level,
              preferred_sport_name:
                  profileV2?.preferred_sport_name ?? userInfo.preferred_sport_name,
              is_staff: userInfo.is_staff,
              brand_name: profileV2?.brand_name ?? null,
              certificates: profileV2?.certificates ?? [],
              best_records: profileV2?.best_records ?? [],
          }
        : null;

    return (
        <div>
            <SubHeader
                title="마이 버클"
                showSettings
                menus={[
                    ...(MESSAGING_ENABLED ? [{
                        name: "메시지",
                        content: badgedIcon(messageIcon, messageUnreadCount),
                        ariaLabel: unreadLabel("메시지", messageUnreadCount),
                        callback: () => navigate("/messages"),
                    }] : []),
                    {
                        name: "알림",
                        content: badgedIcon(alertIcon, notificationCount),
                        ariaLabel: unreadLabel("알림", notificationCount),
                        callback: () => navigate("/alert"),
                    },
                ]}
            />

            <div className="mybuccl-wrap">
                {userInfo === null ? (
                    <CoverPage />
                ) : isProfileLoading ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        <div className="mybuccl-header-wrap">
                            <ProfileCard
                                variant="mybuccle"
                                profile={profile}
                                bucketList={bucketList}
                                onEdit={() => navigate("/mypage/user")}
                                onAvatarClick={() => setProfileViewerOpen(true)}
                            />
                        </div>
                        <TabHeader
                            menus={menus}
                            defaultTab={selectedTab}
                            setSelectedTab={setSelectedTab}
                            marginTop={20}
                        />
                        <TabWrap
                            tabName={selectedTab}
                            onTabChange={setSelectedTab}
                        />
                    </>
                )}
            </div>

            {profileViewerOpen && profile?.profile_image_url && (
                <MediaViewer
                    media={[{ url: profile.profile_image_url, type: "image" }]}
                    initialIndex={0}
                    title={profile.nickname || profile.name}
                    onClose={() => setProfileViewerOpen(false)}
                />
            )}
        </div>
    );
};

export default MyBuccl;
